<script>
import MonthSelect from "../../MonthSelectorComponent";
import ServiceReminderTypeService from "../../../services/ServiceReminderTypeService";
import AuthService from "../../../services/AuthService";
import { mapActions, mapMutations, mapState } from "vuex";

export default {
    components: {
        MonthSelect,
    },
    data() {
        return {
            searchParams: {
                month: window.moment().month() + 1,
                year: window.moment().year(),
                status: "due",
                type: null,
                postcode: null,
            },
            showCompleted: false,
            serviceReminderTypes: [],
        };
    },
    computed: {
        ...mapState("serviceReminders", [
            "reminders",
            "sending",
            "loading",
            "resetting",
        ]),
        searchDate() {
            return window
                .moment()
                .year(this.searchParams.year)
                .month(this.searchParams.month - 1)
                .startOf("month");
        },

        canSendReminders() {
            if (this.sending || this.reminders === undefined) {
                return false;
            }

            return this.reminders.filter((r) => r.status === "due").length > 0;
        },
        isAdmin() {
            return AuthService.user && AuthService.user.admin_access;
        },
    },

    mounted() {
        this.loadServiceReminderTypes();
        this.triggerSearch();
        this.listenForUpdates();
        setTimeout(() => {
            window.$('[data-toggle="tooltip"]').tooltip();
        }, 1000);
    },
    beforeDestroy() {
        this.stopListeningForUpdates();
    },

    methods: {
        ...mapActions("serviceReminders", [
            "searchReminders",
            "sendReminders",
            "sendAllUnsentReminders",
            "resetReminders",
            "refreshReminder",
        ]),
        ...mapMutations("serviceReminders", ["setReminders"]),
        triggerSearch(){
            this.searchReminders(this.searchParams);
        },
        loadServiceReminderTypes() {
            ServiceReminderTypeService.index().then((res) => {
                this.serviceReminderTypes = ServiceReminderTypeService.toOptions(res.data);
            }).catch(() => {
                window.alert('Error: Could not load service reminder types');
            });
        },
        listenForUpdates() {
            window.Echo.private("service-reminders").listen(
                "ServiceReminderUpdated",
                (event) => {
                    this.refreshReminder(event.reminder.id)
                }
            );
        },

        stopListeningForUpdates() {
            window.Echo.leave("service-reminders");
        },
    },
}
</script>

<template>
    <div class="container-fluid">
        <div class="card">
            <div class="card-body">
                <div class="row">
                    <div class="col-sm-12 col-md-6">
                        <p>Service reminder statuses reset to <strong>Due</strong> 2 months before their selected month.</p>
                    </div>
                    <div class="col-sm-12 col-md-6 text-right service-reminders-nav-list">
                        <router-link
                            v-if="isAdmin"
                            :to="{name: 'service_reminder_types'}"
                        >
                            Edit types
                        </router-link>
                        <router-link :to="{name: 'missing_service_reminders'}">
                            Sites Missing Reminders
                        </router-link>
                        <router-link :to="{name: 'service_reminders_map'}">
                            View as Map
                        </router-link>
                        <router-link :to="{name: 'service_reminders'}">
                            View as List
                        </router-link>
                    </div>
                </div>
            </div>
        </div>
        <div class="card">
            <div class="card-body">
                <form
                    action=""
                    @submit.prevent="search"
                >
                    <div class="row">
                        <div class="col-3">
                            <label for="">Customer:</label>
                            <input
                                type="text"
                                v-model="searchParams.customer"
                                @input="triggerSearch"
                                placeholder="Customer Name..."
                                class="form-control"
                            >
                        </div>

                        <div class="col-1">
                            <label for="">Postcode:</label>
                            <input
                                type="text"
                                v-model="searchParams.postcode"
                                @input="triggerSearch"
                                placeholder="Postcode"
                                id="postcode"
                                autocomplete="off"
                                class="form-control"
                            >
                        </div>

                        <div class="col-2">
                            <label for="">Month:</label>
                            <month-select
                                v-model="searchParams.month"
                                @input="triggerSearch"
                            />
                        </div>
                        <div class="col-2">
                            <label>
                                Status:
                            </label>
                            <select
                                name="status"
                                id=""
                                v-model="searchParams.status"
                                @change="triggerSearch"
                                class="form-control"
                            >
                                <option :value="null">
                                    All statuses
                                </option>
                                <option value="due">
                                    Due
                                </option>
                                <option value="booked">
                                    Booked
                                </option>
                                <option value="ok any">
                                    OK Any
                                </option>
                                <option value="done">
                                    Done
                                </option>
                                <option value="no access">
                                    No Access
                                </option>
                            </select>
                        </div>

                        <div class="col-2">
                            <label>
                                Type:
                            </label>
                            <select
                                name="type"
                                id=""
                                v-model="searchParams.type"
                                @change="triggerSearch"
                                class="form-control"
                            >
                                <option :value="null">
                                    All Types
                                </option>
                                <option
                                    v-for="type in serviceReminderTypes"
                                    :key="type.value"
                                    :value="type.value"
                                >
                                    {{ type.label }}
                                </option>
                            </select>
                        </div>

                        <div class="col-2">
                            <br>
                            <button
                                class="btn btn-primary"
                                @click.prevent="triggerSearch"
                            >
                                <i
                                    class="fas fa-sync-alt"
                                    :class="{'fa-spin': loading}"
                                />
                            </button>

                            <button
                                class="btn btn-info"
                                :disabled="!canSendReminders"
                                @click.prevent="sendAllUnsentReminders"
                            >
                                <i
                                    v-show="!sending"
                                    class="far fa-envelope"
                                />
                                <i
                                    v-show="sending"
                                    class="fa fa-sync-alt fa-spin"
                                />
                                Send reminders
                            </button>
                        </div>
                    </div>
                    <br>
                </form>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.service-reminders-nav-list {
    display: flex;
    gap: 1.5rem;
    align-items: center;
    justify-content: flex-end;
}
</style>


// WEBPACK FOOTER //
// resources/assets/js/components/office/service-reminders/SearchComponent.vue